import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { ProjectImage } from "../types";
import { imageUrl } from "../utils";
import dayjs from "dayjs";
import {
  AccountCircle,
  AspectRatio,
  Camera,
  CameraAlt,
  Close,
  CloudUpload,
  Delete,
  Description,
  Download,
  Edit,
  Label,
  X,
  ZoomInMap,
  ZoomOutMap,
} from "@mui/icons-material";
import DeleteButton from "@/shared/dialogs/DeleteButton";
import { setPowersetDependencies } from "mathjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiService from "@/api/ApiService";
import { LoadingButton } from "@mui/lab";

type Props = {
  images: ProjectImage[];
  startImageId: number;
  onClose: () => void;
};

export function GalleryDetail({ images, startImageId, onClose }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");

  const [scrollView, setScrollView] = useState(false);

  const sortedImages = useMemo(() => {
    return [...images].sort((a, b) => {
      const da = a.madeAt ?? a.createdAt;
      const db = b.madeAt ?? b.createdAt;
      return db > da ? 1 : -1;
    });
  }, [images]);

  useEffect(() => {
    if (!sortedImages.length) return;
    const idx = sortedImages.findIndex((img) => img.id === startImageId);
    setCurrentIndex(idx >= 0 ? idx : 0);
  }, [sortedImages, startImageId]);

  if (!sortedImages.length) {
    return null;
  }

  const current = sortedImages[currentIndex];
  const queryClient = useQueryClient();

  const editDescriptionMutation = useMutation({
    mutationFn: ApiService.editEntity("projectImage", current?.id ?? 0),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getProjectImages"],
      });
      setOpen(false);
      setDescription("");
    },
  });

  const goPrev = () => {
    setScrollView(false);
    setCurrentIndex((idx) => (idx > 0 ? idx - 1 : idx));
  };

  const goNext = () => {
    setScrollView(false);
    setCurrentIndex((idx) => (idx < sortedImages.length - 1 ? idx + 1 : idx));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goPrev();
      }

      if (e.key === "ArrowRight") {
        goNext();
      }

      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [goPrev, goNext, onClose]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", flex: 1 }}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.900",
          flex: 1,
          overflow: "hidden",
          minHeight: 360,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: scrollView ? "flex-start" : "center",
            justifyContent: scrollView ? "flex-start" : "center",
            overflowY: scrollView ? "auto" : "hidden",
          }}
        >
          <Box
            key={current.filename}
            component="img"
            src={scrollView ? imageUrl(current.filename.replace(/(\.[^.]+)$/, "_full$1")) : imageUrl(current.filename)}
            alt={current.description || current.originalFilename}
            sx={{
              maxHeight: scrollView ? "none" : "95%",
              maxWidth: scrollView ? "none" : "100%",
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        </Box>
        <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", gap: "8px" }}>
          <IconButton
            aria-label="Zoom"
            onClick={() => setScrollView((s) => !s)}
            sx={{
              borderStyle: "solid",
              borderColor: theme.palette.text.primary,
              borderWidth: 1,
              backgroundColor: theme.palette.background.paper,
              "&:hover": { backgroundColor: theme.palette.background.paper },
            }}
          >
            {scrollView ? <ZoomInMap /> : <ZoomOutMap />}
          </IconButton>
          <DeleteButton
            key={current.id}
            xsx={{
              borderStyle: "solid",
              borderColor: theme.palette.text.primary,
              borderWidth: 1,
              backgroundColor: theme.palette.error.light,
              "&:hover": { backgroundColor: theme.palette.error.main },
            }}
            invalidate={["getProjectImages"]}
            sideEffect={currentIndex >= sortedImages.length - 1 ? onClose : goNext}
            type={"projectImage"}
            id={current.id}
          />
        </Box>
        <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: "8px" }}>
          <IconButton
            aria-label="Schließen"
            onClick={onClose}
            sx={{
              borderStyle: "solid",
              borderColor: theme.palette.text.primary,
              borderWidth: 1,
              backgroundColor: theme.palette.background.paper,
              "&:hover": { backgroundColor: theme.palette.background.paper },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <IconButton
          aria-label="Vorheriges Bild"
          onClick={goPrev}
          disabled={currentIndex === 0}
          sx={{
            position: "absolute",
            left: 8,
            borderStyle: "solid",
            borderColor: theme.palette.text.primary,
            borderWidth: 1,
            backgroundColor: theme.palette.background.paper,
            "&:hover": { backgroundColor: theme.palette.background.paper },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <IconButton
          aria-label="Nächstes Bild"
          onClick={goNext}
          disabled={currentIndex >= sortedImages.length - 1}
          sx={{
            position: "absolute",
            right: 8,
            borderStyle: "solid",
            borderColor: theme.palette.text.primary,
            borderWidth: 1,
            backgroundColor: theme.palette.background.paper,
            "&:hover": { backgroundColor: theme.palette.background.paper },
          }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          bgcolor: "grey.900",
          color: "white",
          flexWrap: "wrap",
          justifyContent: "space-between",
          fontSize: "80%",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Label fontSize="inherit" />
          <Box>{current.description || <span style={{ fontStyle: "italic", opacity: 0.5 }}>Ohne Beschreibung</span>}</Box>
          <Edit
            fontSize="inherit"
            onClick={() => {
              setDescription(current.description);
              setOpen(true);
            }}
            sx={{ cursor: "pointer", "&:hover": { color: "#aaa" } }}
          />
        </Box>

        {current.madeAt ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CameraAlt fontSize="inherit" />
            <Box>{dayjs.utc(current.madeAt).local().format("ddd, DD.MM.YYYY HH:mm")} Uhr</Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CloudUpload fontSize="inherit" />
            <Box>{dayjs.utc(current.createdAt).local().format("ddd, DD.MM.YYYY HH:mm")} Uhr</Box>
          </Box>
        )}
        {!!current.createdBy ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <AccountCircle fontSize="inherit" />
            <Box>{current.createdBy.firstName + " " + current.createdBy.name}</Box>
          </Box>
        ) : null}
        <Box>{currentIndex + 1 + " / " + sortedImages.length}</Box>
      </Box>
      <Dialog
        fullWidth
        open={open}
        onClose={() => {
          setOpen(false);
          setDescription("");
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Beschreibung bearbeiten</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Beschreibung" />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setDescription("");
            }}
          >
            Abbrechen
          </Button>
          <LoadingButton
            loading={editDescriptionMutation.isPending}
            variant="contained"
            onClick={() => editDescriptionMutation.mutate({ description })}
          >
            Speichern
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
